import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import { toMap } from '../../../../utils/utils';
import PixelInput from '../../../general-components/pixel-input';
import { FIXED_LAYOUTS, PLAIN_LAYOUTS } from '../../../../utils/video-layout';
import {
	AppSettings,
	LayoutSpacing,
	LayoutSpacingKey,
	LayoutSpacingPreset,
} from '../../../../stores/reducers/settings';

interface Props {
	queue: AppSettings;
	disabled: boolean;
	previewLayout: string;
	settingsForShotEdited: (newQueue: any) => void;
}

export default function Spacing(props: Props) {
	const { 
		queue,
		disabled,
		previewLayout,
		settingsForShotEdited
	} = props;

	const layout = useMemo((): LayoutSpacingPreset => {
		const layoutMap: Map<string, LayoutSpacingPreset> = toMap(
			'layout',
			queue.layoutSpacingPresets,
		);
		const visibleLayout = previewLayout;
		// if no layout exists set basic PresetLayout interface
		const presetLayout = layoutMap.get(visibleLayout);
		const backup: LayoutSpacingPreset = {
			layout: previewLayout,
			isLinked: false,
			spacing: {
				top: 0,
				bottom: 0,
				side: 0,
				inner: 0,
			},
		};

		return presetLayout?.spacing ? presetLayout : backup;
	}, [queue, previewLayout]);

	function setSpacingPreset(type: LayoutSpacingKey | 'isLinked', value: number) {
		const newPresetLayout: LayoutSpacingPreset = { ...layout };

		if (type === 'isLinked') {
			newPresetLayout.isLinked = !newPresetLayout.isLinked;
			newPresetLayout.spacing = {
				top: newPresetLayout.spacing.top,
				bottom: newPresetLayout.spacing.top,
				side: newPresetLayout.spacing.top,
				inner: newPresetLayout.spacing.top,
			};
		} else {
			let spacing: LayoutSpacing;

			if (newPresetLayout.isLinked) {
				spacing = {
					top: value, bottom: value, side: value, inner: value,
				};
			} else {
				spacing = { ...newPresetLayout.spacing };
				spacing[type] = value;
			}

			newPresetLayout.spacing = spacing;
		}

		let currentLayoutSpacingPresets = [...queue.layoutSpacingPresets];
		const len = currentLayoutSpacingPresets.length;

		for (let i = 0; i <= len; i += 1) {
			if (currentLayoutSpacingPresets[i]?.layout === newPresetLayout.layout) {
				// set new preset
				currentLayoutSpacingPresets[i] = { ...newPresetLayout };
				break;
			} else if (i === len) {
				// no preset found, add to list
				currentLayoutSpacingPresets = [...currentLayoutSpacingPresets, newPresetLayout];
			}
		}

		const newQueue = { ...queue, layoutSpacingPresets: currentLayoutSpacingPresets };

		settingsForShotEdited(newQueue);
	}

	function topChange(e: number) { setSpacingPreset('top', e); }

	function bottomChange(e: number) { setSpacingPreset('bottom', e); }

	function sidesChange(e: number) { setSpacingPreset('side', e); }

	function innerChange(e: number) { setSpacingPreset('inner', e); }

	function toggleSpacingLinked() { setSpacingPreset('isLinked', layout.spacing.top); }

	return (
		<div className={`settings-container spacing-container ${disabled ? 'disabled' : ''}`}>
			<div className="align-items-center justify-content-space-between margin-bottom-medium">
				<label className="section-title">
					<span className={classNames('title-text')}>Layout Spacing</span>
				</label>
				<button className="label-action link-button" onClick={toggleSpacingLinked}>
					{layout.isLinked
						? (<><>Unlink &nbsp;<span style={{ verticalAlign: 'middle' }}><Icon name={ICONS.LINK} color={COLORS.DEFAULT_GRAY} size={20} /></span></></>)
						: (<><>Link &nbsp;</><span style={{ verticalAlign: 'middle' }}><Icon name={ICONS.UNLINK} color={COLORS.DEFAULT_GRAY} size={20} /></span></>)}
				</button>
			</div>
			<div className="settings-actions">
				<div className="spacing">
					<div className="label-wrapper">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect y="4" width="16" height="12" rx="1" fill="#333333" />
							<rect width="16" height="2" rx="1" fill="#878793" />
						</svg>

						<label>Top</label>
					</div>
					<PixelInput
						onChange={topChange}
						value={layout.spacing.top}
					/>
				</div>
				<div className="spacing">
					<div className="label-wrapper">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect width="16" height="12" rx="1" transform="matrix(1 0 0 -1 0 12)" fill="#333333" />
							<rect width="16" height="2" rx="1" transform="matrix(1 0 0 -1 0 16)" fill="#878793" />
						</svg>

						<label>Bottom</label>
					</div>
					<PixelInput
						onChange={bottomChange}
						value={layout.spacing.bottom}
					/>
				</div>
				<div className="spacing">
					<div className="label-wrapper">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect x="4" width="8" height="16" rx="1" fill="#333333" />
							<rect x="14" width="2" height="16" rx="1" fill="#878793" />
							<rect width="2" height="16" rx="1" fill="#878793" />
						</svg>

						<label>Sides</label>
					</div>
					<PixelInput
						onChange={sidesChange}
						value={layout.spacing.side}
					/>
				</div>
				<div className="spacing">
					<div className="label-wrapper">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect x="11" y="11" width="5" height="5" rx="1" fill="#333333" />
							<rect y="11" width="5" height="5" rx="1" fill="#333333" />
							<rect x="11" width="5" height="5" rx="1" fill="#333333" />
							<rect width="5" height="5" rx="1" fill="#333333" />
							<rect x="7" width="2" height="16" rx="1" fill="#878793" />
							<rect y="7" width="16" height="2" rx="1" fill="#878793" />
						</svg>
						<label>Inside</label>
					</div>
					<PixelInput
						onChange={innerChange}
						value={layout.spacing.inner}
					/>
				</div>
			</div>
			<label className="spacing subsection-title margin-bottom-small">Sets the selected layout's spacing</label>
		</div>
	);
}
